import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Route } from '@/constants';
import {
    useDeleteUserByIdMutation,
    useGetUsersQuery,
    useUpdateUserByIdMutation,
} from '@/features/slice/users';
import { toastError } from '@/utils';

import { useUrlFilter } from '../../../hooks';

export const useUserList = () => {
    const { debouncedSearchText } = useUrlFilter();

    const { data, isSuccess, isError, error, isLoading, isFetching } =
        useGetUsersQuery({
            search: debouncedSearchText,
        });

    const [updateUser, { isLoading: isUpdating }] = useUpdateUserByIdMutation();

    const [deleteUser, { isLoading: isDeleting }] = useDeleteUserByIdMutation();

    const navigate = useNavigate();

    const handleSuspendUser = async (userId: string) => {
        try {
            await updateUser({
                userId,
                data: { status: 'suspended' },
            }).unwrap();
            toast.success('User suspended successfully.');
        } catch (error) {
            toastError(
                error,
                'useUserList -> handleSuspendUser: Failed to suspend user.',
            );
        }
    };

    const handleActivateUser = async (userId: string) => {
        try {
            await updateUser({
                userId,
                data: { status: 'active' },
            }).unwrap();
            toast.success('User activated successfully.');
        } catch (error) {
            toastError(error, 'useUserList -> handleActivateUser');
        }
    };

    const handleDeleteUser = async (userId: string) => {
        try {
            await deleteUser(userId).unwrap();
            toast.success('User deleted successfully.');
        } catch (error) {
            toastError(
                error,
                'useUserList -> handleDeleteUser: Failed to delete user.',
            );
        }
    };

    const handleEditUser = (userId: string) => {
        navigate(`${Route.USERS}/${userId}/edit`);
    };

    return {
        data,
        isSuccess,
        isError,
        error,
        isLoading,
        isFetching,

        handleSuspendUser,
        handleActivateUser,
        handleEditUser,

        handleDeleteUser,
        isDeleting,

        updateUser,
        isUpdating,
    };
};
